const DESKTOP_MEDIA_QUERY = '(hover: hover) and (pointer: fine) and (min-width: 750px)';
const ROOT_SELECTOR = '[data-pdp-related-title-root]';
const ITEM_SELECTOR = '[data-pdp-related-item]';
const BADGE_SELECTOR = '[data-pdp-related-badge]';

class PdpRelatedProductsTitleCursor {
  #raf = 0;
  #pending = null;
  #activeRoot = null;
  #boundPointerMove;
  #boundPointerLeave;
  #boundScrollHide;

  constructor() {
    this.#boundPointerMove = this.#handlePointerMove.bind(this);
    this.#boundPointerLeave = this.#handlePointerLeave.bind(this);
    this.#boundScrollHide = this.#hideBadge.bind(this);
  }

  init() {
    document.addEventListener('pointermove', this.#boundPointerMove);
    document.addEventListener('pointerleave', this.#boundPointerLeave);
    window.addEventListener('scroll', this.#boundScrollHide, { passive: true });
    window.addEventListener('blur', this.#boundScrollHide);
  }

  #isDesktop() {
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
  }

  #handlePointerMove(event) {
    if (!this.#isDesktop()) {
      this.#hideBadge();
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) {
      this.#hideBadge();
      return;
    }

    const item = target.closest(ITEM_SELECTOR);
    if (!(item instanceof HTMLElement)) {
      this.#hideBadge();
      return;
    }

    const root = item.closest(ROOT_SELECTOR);
    if (!(root instanceof HTMLElement)) {
      this.#hideBadge();
      return;
    }

    const badge = root.querySelector(BADGE_SELECTOR);
    if (!(badge instanceof HTMLElement)) {
      this.#hideBadge();
      return;
    }

    const productTitle = item.dataset.productTitle?.trim();
    if (!productTitle) {
      this.#hideBadge();
      return;
    }

    this.#activeRoot = root;
    badge.textContent = productTitle;
    this.#pending = {
      badge,
      x: event.clientX,
      y: event.clientY,
    };

    if (this.#raf) return;

    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      const pending = this.#pending;
      if (!pending) return;

      const { badge: activeBadge, x: pointerX, y: pointerY } = pending;
      const offset = 14;
      const pad = 8;
      let x = pointerX + offset;
      let y = pointerY + offset;

      activeBadge.classList.add('is-visible');

      const width = activeBadge.offsetWidth;
      const height = activeBadge.offsetHeight;
      const maxX = window.innerWidth - width - pad;
      const maxY = window.innerHeight - height - pad;
      x = Math.min(Math.max(pad, x), maxX);
      y = Math.min(Math.max(pad, y), maxY);

      activeBadge.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    });
  }

  #handlePointerLeave() {
    this.#hideBadge();
  }

  #hideBadge() {
    if (this.#raf) {
      cancelAnimationFrame(this.#raf);
      this.#raf = 0;
    }
    this.#pending = null;

    if (!(this.#activeRoot instanceof HTMLElement)) return;

    const badge = this.#activeRoot.querySelector(BADGE_SELECTOR);
    if (badge instanceof HTMLElement) {
      badge.classList.remove('is-visible');
    }
    this.#activeRoot = null;
  }
}

if (!window.__pdpRelatedProductsTitleCursor) {
  const cursor = new PdpRelatedProductsTitleCursor();
  window.__pdpRelatedProductsTitleCursor = cursor;
  cursor.init();
}
